// src/components/faqs/FAQForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  createFaq,
  updateFaq,
  deleteFaqDocument,
  fetchFaq,
  fetchFaqCategories,
  fetchFaqTags,
  uploadFaqImage,
  clearFaqError,
  clearFaqSuccess,
} from '../../store/actions/faqActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import DocumentUploadModal from '../common/DocumentUploadModal';
import DocumentGallery from '../common/DocumentGallery';

const FAQForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedFaq, loading, error, success, categories, tags } = useSelector(
    (state) => state.faqs
  );
  const { user } = useSelector((state) => state.auth || { user: null });

  const [formData, setFormData] = useState({
    question: '',
    slug: '',
    answer: '',
    status: 'DRAFT',
    priority: 0,
    categoryId: '',
    tagIds: [],
    metaTitle: '',
    metaDescription: '',
    keywords: [],
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [keywordInput, setKeywordInput] = useState('');
  const [images, setImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documents, setDocuments] = useState([]);

  const isEditMode = !!id;

  const statusOptions = [
    { value: 'DRAFT', label: 'Brouillon' },
    { value: 'PENDING_REVIEW', label: 'En attente de relecture' },
    { value: 'PUBLISHED', label: 'Publié' },
    { value: 'SCHEDULED', label: 'Programmé' },
    { value: 'ARCHIVED', label: 'Archivé' },
  ];

  const tabs = [
    { id: 'content', label: 'Contenu' },
    { id: 'seo', label: 'SEO' },
    { id: 'settings', label: 'Paramètres' },
  ];

  // Quill editor modules
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link', 'image', 'video'],
      ['blockquote', 'code-block'],
      ['clean'],
    ],
  };

  // Styles object
  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
    },
    tab: {
      padding: '1rem 1.5rem',
      background: 'none',
      border: 'none',
      borderBottom: '2px solid transparent',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    tabActive: {
      color: '#2563eb',
      borderBottomColor: '#2563eb',
      backgroundColor: '#ffffff',
    },
    content: {
      padding: '2rem',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem',
    },
    fullWidth: {
      gridColumn: 'span 2',
    },
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    select: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      backgroundColor: '#ffffff',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    slugPreview: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    editor: {
      height: '400px',
      marginBottom: '3rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    imageUpload: {
      marginTop: '1rem',
      padding: '1.5rem',
      border: '2px dashed #d1d5db',
      borderRadius: '0.5rem',
      textAlign: 'center',
      backgroundColor: '#f9fafb',
      cursor: 'pointer',
    },
    imageUploadButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    imagePreviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
      gap: '1rem',
      marginTop: '1rem',
    },
    imagePreview: {
      width: '100%',
      height: '100px',
      borderRadius: '0.5rem',
      objectFit: 'cover',
      border: '1px solid #e5e7eb',
    },
    keywordsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
      padding: '0.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
    },
    keyword: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      backgroundColor: '#e5e7eb',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
    },
    removeKeyword: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      fontSize: '0.875rem',
      padding: '0 0.25rem',
    },
    tagsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    tag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.25rem 0.5rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    tagSelected: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
      paddingTop: '2rem',
      borderTop: '1px solid #e5e7eb',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  useEffect(() => {
    dispatch(fetchFaqCategories({ limit: 100 }));
    dispatch(fetchFaqTags({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (selectedFaq) {
      setDocuments(selectedFaq.documents || []);
      setImages(selectedFaq.images || []);
    }
  }, [selectedFaq]);

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchFaq(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (isEditMode && selectedFaq) {
      setFormData({
        question: selectedFaq.question || '',
        slug: selectedFaq.slug || '',
        answer: selectedFaq.answer || '',
        status: selectedFaq.status || 'DRAFT',
        priority: selectedFaq.priority || 0,
        categoryId: selectedFaq.categoryId || '',
        tagIds: selectedFaq.tags?.map(t => t.id) || [],
        metaTitle: selectedFaq.metaTitle || '',
        metaDescription: selectedFaq.metaDescription || '',
        keywords: selectedFaq.keywords || [],
      });
      setImages(selectedFaq.images || []);
    }
  }, [selectedFaq, isEditMode]);

  // Generate slug from question
  useEffect(() => {
    if (!isEditMode && formData.question && !formData.slug) {
      const slug = formData.question
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.question, isEditMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearFaqError());
      dispatch(clearFaqSuccess());
    };
  }, [dispatch]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.question.trim()) {
      newErrors.question = 'La question est requise';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Le slug est requis';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Le slug ne peut contenir que des lettres minuscules, des chiffres et des tirets';
    }

    if (!formData.answer.trim()) {
      newErrors.answer = 'La réponse est requise';
    }

    if (formData.priority < 0 || formData.priority > 10) {
      newErrors.priority = 'La priorité doit être entre 0 et 10';
    }

    if (formData.metaDescription && formData.metaDescription.length > 160) {
      newErrors.metaDescription = 'La meta description ne doit pas dépasser 160 caractères';
    }

    return newErrors;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  }, [errors]);

  const handleEditorChange = (content) => {
    setFormData(prev => ({ ...prev, answer: content }));
    if (errors.answer) {
      setErrors(prev => ({ ...prev, answer: undefined }));
    }
  };

  const handleDocumentUploadComplete = (uploadedDocs) => {
    setDocuments(prev => [...prev, ...uploadedDocs]);
    setImages(prev => [...prev, ...uploadedDocs.filter(doc => doc.fileType?.startsWith('image/'))]);
  };

  const handleDocumentDelete = async (doc) => {
    try {
      await dispatch(deleteFaqDocument(id, doc.id));
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      setImages(prev => prev.filter(d => d.id !== doc.id));
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()],
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword),
    }));
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tagIds: prev.tagIds.includes(tagId)
        ? prev.tagIds.filter(id => id !== tagId)
        : [...prev.tagIds, tagId],
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    try {
      for (const file of files) {
        if (isEditMode && id) {
          await dispatch(uploadFaqImage(id, file));
        } else {
          // Store images locally for creation (will be uploaded after FAQ is created)
          const reader = new FileReader();
          reader.onloadend = () => {
            setImages(prev => [...prev, { preview: reader.result, file }]);
          };
          reader.readAsDataURL(file);
        }
      }
    } catch (error) {
      console.error('Image upload failed:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // Find first tab with error
      if (validationErrors.question || validationErrors.slug || validationErrors.answer) {
        setActiveTab('content');
      } else if (validationErrors.metaTitle || validationErrors.metaDescription) {
        setActiveTab('seo');
      } else {
        setActiveTab('settings');
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const faqData = {
        ...formData,
        authorId: user?.id,
      };

      let faqResponse;
      if (isEditMode) {
        faqResponse = await dispatch(updateFaq(id, faqData));
      } else {
        faqResponse = await dispatch(createFaq(faqData));
      }

      // Upload images if any (for new FAQs)
      if (!isEditMode && images.length > 0 && faqResponse?.id) {
        for (const image of images) {
          if (image.file) {
            await dispatch(uploadFaqImage(faqResponse.id, image.file));
          }
        }
      }

      navigate('/admin/faqs');
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, id, isEditMode, navigate, validateForm, images, user]);

  const handleCancel = useCallback(() => {
    const hasChanges = Object.keys(formData).some(key => {
      if (key === 'tagIds') return formData[key].length > 0;
      if (key === 'keywords') return formData[key].length > 0;
      if (key === 'priority') return formData[key] !== 0;
      return formData[key] !== '' && formData[key] !== 'DRAFT';
    }) || images.length > 0;

    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      navigate('/admin/faqs');
    }
  }, [formData, images, navigate]);

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? 'Modifier la FAQ' : 'Créer une nouvelle FAQ'}
        </h1>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearFaqError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearFaqSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Tabs */}
        <div style={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={styles.content}>
          {/* Content Tab */}
          <div style={activeTab === 'content' ? {} : { display: 'none' }}>
            <div style={styles.grid}>
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="question">
                    Question *
                  </label>
                  <input
                    type="text"
                    id="question"
                    name="question"
                    value={formData.question}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ...(errors.question ? styles.inputError : {}),
                    }}
                    placeholder="Question fréquemment posée"
                  />
                  {errors.question && (
                    <div style={styles.errorText}>{errors.question}</div>
                  )}
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="slug">
                    Slug *
                  </label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ...(errors.slug ? styles.inputError : {}),
                    }}
                    placeholder="question-frequente"
                  />
                  {errors.slug && (
                    <div style={styles.errorText}>{errors.slug}</div>
                  )}
                  <div style={styles.slugPreview}>
                    URL: /faq/{formData.slug || 'question-frequente'}
                  </div>
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="answer">
                    Réponse *
                  </label>
                  <div style={styles.editor}>
                    <ReactQuill
                      theme="snow"
                      value={formData.answer}
                      onChange={handleEditorChange}
                      modules={quillModules}
                      placeholder="Rédigez la réponse ici..."
                    />
                  </div>
                  {errors.answer && (
                    <div style={styles.errorText}>{errors.answer}</div>
                  )}
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <label style={styles.label}>Documents et images</label>
                    <button
                      type="button"
                      onClick={() => setShowDocumentModal(true)}
                      style={styles.imageUploadButton}
                    >
                      <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Ajouter des documents
                    </button>
                  </div>

                  <DocumentGallery
                    documents={documents}
                    entityId={id}
                    entityType="faq"
                    onDelete={handleDocumentDelete}
                  />
                </div>
              </div>
              {/* Images */}
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Images</label>
                  <div
                    style={styles.imageUpload}
                    onClick={() => document.getElementById('faq-images').click()}
                  >
                    <input
                      type="file"
                      id="faq-images"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <svg style={{ width: '2rem', height: '2rem', marginBottom: '0.5rem', color: '#9ca3af' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem' }}>
                      Cliquez pour ajouter des images
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      PNG, JPG, GIF (max. 5 Mo)
                    </div>
                  </div>
                  {(images.length > 0 || selectedFaq?.images?.length > 0) && (
                    <div style={styles.imagePreviewGrid}>
                      {(images || selectedFaq?.images || []).map((img, index) => (
                        <img
                          key={index}
                          src={img.preview || img.storagePath}
                          alt={`Preview ${index}`}
                          style={styles.imagePreview}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* SEO Tab */}
          <div style={activeTab === 'seo' ? {} : { display: 'none' }}>
            <div style={styles.grid}>
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="metaTitle">
                    Meta titre
                  </label>
                  <input
                    type="text"
                    id="metaTitle"
                    name="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleChange}
                    style={styles.input}
                    placeholder="Titre pour les moteurs de recherche"
                  />
                  <div style={styles.helperText}>
                    Laissez vide pour utiliser la question
                  </div>
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label} htmlFor="metaDescription">
                    Meta description
                  </label>
                  <textarea
                    id="metaDescription"
                    name="metaDescription"
                    value={formData.metaDescription}
                    onChange={handleChange}
                    style={{
                      ...styles.textarea,
                      ...(errors.metaDescription ? styles.inputError : {}),
                    }}
                    placeholder="Description pour les moteurs de recherche"
                    rows="3"
                  />
                  {errors.metaDescription && (
                    <div style={styles.errorText}>{errors.metaDescription}</div>
                  )}
                  <div style={styles.helperText}>
                    {formData.metaDescription?.length || 0}/160 caractères
                  </div>
                </div>
              </div>

              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Mots-clés</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                      style={{ ...styles.input, flex: 1 }}
                      placeholder="Ajouter un mot-clé"
                    />
                    <button
                      type="button"
                      onClick={handleAddKeyword}
                      style={styles.imageUploadButton}
                    >
                      Ajouter
                    </button>
                  </div>
                  {formData.keywords.length > 0 && (
                    <div style={styles.keywordsContainer}>
                      {formData.keywords.map((keyword, index) => (
                        <span key={index} style={styles.keyword}>
                          {keyword}
                          <button
                            type="button"
                            onClick={() => handleRemoveKeyword(keyword)}
                            style={styles.removeKeyword}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Settings Tab */}
          <div style={activeTab === 'settings' ? {} : { display: 'none' }}>
            <div style={styles.grid}>
              {/* Status */}
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="status">
                  Statut
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  style={styles.select}
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="priority">
                  Priorité (0-10)
                </label>
                <input
                  type="number"
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  min="0"
                  max="10"
                  style={{
                    ...styles.input,
                    ...(errors.priority ? styles.inputError : {}),
                  }}
                />
                {errors.priority && (
                  <div style={styles.errorText}>{errors.priority}</div>
                )}
                <div style={styles.helperText}>
                  Plus le chiffre est élevé, plus la FAQ est importante
                </div>
              </div>

              {/* Category */}
              <div style={styles.fieldGroup}>
                <label style={styles.label} htmlFor="categoryId">
                  Catégorie
                </label>
                <select
                  id="categoryId"
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div style={styles.fullWidth}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Tags</label>
                  <div style={styles.tagsContainer}>
                    {tags.map(tag => (
                      <span
                        key={tag.id}
                        style={{
                          ...styles.tag,
                          ...(formData.tagIds.includes(tag.id) ? styles.tagSelected : {}),
                        }}
                        onClick={() => handleTagToggle(tag.id)}
                      >
                        {tag.name}
                        {formData.tagIds.includes(tag.id) && ' ✓'}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div style={styles.buttonGroup}>
            <button
              type="button"
              onClick={handleCancel}
              style={styles.cancelButton}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting || uploadingImage}
              style={{
                ...styles.submitButton,
                ...((isSubmitting || uploadingImage) ? styles.submitButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && !uploadingImage) {
                  e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting && !uploadingImage) {
                  e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
                }
              }}
            >
              {isSubmitting || uploadingImage
                ? (uploadingImage ? 'Téléchargement...' : (isEditMode ? 'Modification...' : 'Création...'))
                : (isEditMode ? 'Mettre à jour' : 'Créer la FAQ')}
            </button>
          </div>
        </div>
      </form>
      {showDocumentModal && (
        <DocumentUploadModal
          entityId={id}
          entityType="faq"
          onClose={() => setShowDocumentModal(false)}
          onUploadComplete={handleDocumentUploadComplete}
          multiple={true}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          maxSize={10 * 1024 * 1024} // 10MB
        />
      )}
      {/* Cancel Confirmation */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate('/admin/faqs')}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default FAQForm;